import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  FileSearch, 
  BookOpen, 
  Camera, 
  MessageCircle,
  Shield,
  Clock,
  Lightbulb,
  CheckCircle,
  ArrowRight,
  Sparkles,
  AlertTriangle,
  Euro,
  Target,
  Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LeonAnimated from '../components/LeonAnimated';

// Les 4 piliers
const features = [
  {
    icon: FileSearch,
    title: 'Analyser un devis',
    description: 'Détection d\'anomalies, oublis et incohérences',
    details: 'Uploadez un devis PDF et Léon vérifie tout : postes manquants, prix anormaux, erreurs techniques.',
    color: 'blue',
    gradient: 'from-blue-500/20 to-blue-600/10',
    iconBg: 'bg-blue-500/20',
    iconColor: 'text-blue-400'
  },
  {
    icon: BookOpen,
    title: 'Créer une recette travaux',
    description: 'Étapes claires, méthode simple',
    details: 'Décrivez votre projet et recevez un guide pas à pas avec outils, matériaux et conseils.',
    color: 'emerald',
    gradient: 'from-emerald-500/20 to-emerald-600/10',
    iconBg: 'bg-emerald-500/20',
    iconColor: 'text-emerald-400'
  },
  {
    icon: Camera,
    title: 'Diagnostic photo',
    description: 'Analyse rapide + recommandations',
    details: 'Prenez une photo et Léon identifie les problèmes avec des recommandations concrètes.',
    color: 'purple',
    gradient: 'from-purple-500/20 to-purple-600/10',
    iconBg: 'bg-purple-500/20',
    iconColor: 'text-purple-400'
  },
  {
    icon: MessageCircle,
    title: 'Assistant travaux',
    description: 'Réponses instantanées 24h/24',
    details: 'Posez n\'importe quelle question sur vos travaux et obtenez une réponse claire.',
    color: 'gold',
    gradient: 'from-gold-500/20 to-amber-500/10',
    iconBg: 'bg-gold-500/20',
    iconColor: 'text-gold-400'
  }
];

// Avantages
const benefits = [
  {
    icon: AlertTriangle,
    title: 'Éviter les erreurs coûteuses',
    description: 'Détectez les problèmes avant qu\'ils ne vous coûtent cher.',
    color: 'text-red-400',
    bg: 'bg-red-500/10'
  },
  {
    icon: Clock,
    title: 'Gagner du temps',
    description: 'Obtenez des réponses en quelques secondes, pas en heures.',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10'
  },
  {
    icon: Lightbulb,
    title: 'Mieux comprendre',
    description: 'Des explications claires, même pour les débutants.',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10'
  },
  {
    icon: Shield,
    title: 'Décider en confiance',
    description: 'Signez un devis en sachant exactement ce que vous payez.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10'
  }
];

export default function HowItWorks() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleGetStarted = () => {
    if (user) {
      navigate('/analyze');
    } else {
      navigate('/auth');
    }
  };

  return (
    <div className="min-h-screen bg-black-rich pt-20">
      
      {/* Section 1: Accroche */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <LeonAnimated size={100} animate={true} />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl font-heading font-bold text-white mb-6"
          >
            Comment Léon vous aide à{' '}
            <span className="text-gold-500">sécuriser vos travaux</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed"
          >
            Léon est votre assistant intelligent qui vérifie vos devis, 
            vous guide dans vos travaux et répond à toutes vos questions.
            <span className="text-white font-medium"> Simple, rapide, fiable.</span>
          </motion.p>
        </div>
      </section>

      {/* Section 2: Les 4 fonctionnalités */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-zinc-900/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-gold-500 text-sm font-semibold uppercase tracking-wider">
              4 outils puissants
            </span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mt-2">
              Ce que Léon peut faire pour vous
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`glass-card p-6 border border-zinc-700/50 hover:border-${feature.color}-500/30 transition-all group`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-2xl ${feature.iconBg} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-7 h-7 ${feature.iconColor}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1 group-hover:text-gold-400 transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-gold-500 font-medium text-sm mb-2">
                        {feature.description}
                      </p>
                      <p className="text-zinc-400 text-sm leading-relaxed">
                        {feature.details}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 3: Pourquoi utiliser Léon */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-gold-500 text-sm font-semibold uppercase tracking-wider">
              Pourquoi Léon ?
            </span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mt-2">
              Les avantages concrets
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card-dark p-5 text-center hover:border-zinc-600 transition-colors"
                >
                  <div className={`w-12 h-12 rounded-xl ${benefit.bg} flex items-center justify-center mx-auto mb-4`}>
                    <Icon className={`w-6 h-6 ${benefit.color}`} />
                  </div>
                  <h3 className="text-white font-bold mb-2">{benefit.title}</h3>
                  <p className="text-zinc-500 text-sm">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 4: Les 4 guides pratiques */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-zinc-900/50 to-transparent">
        <div className="max-w-4xl mx-auto space-y-16">
          
          {/* Guide 1: Analyser un devis */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <span className="text-blue-400 text-sm font-semibold uppercase tracking-wider">
                En pratique
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mt-2">
                Analyser un devis en 3 étapes
              </h2>
            </motion.div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              {[
                { step: '1', text: 'Uploadez votre devis PDF', icon: '📄' },
                { step: '2', text: 'Léon analyse en 30 secondes', icon: '🔍' },
                { step: '3', text: 'Recevez un diagnostic clair', icon: '✅' }
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="glass-card p-4 sm:p-5 text-center flex-shrink-0 min-w-[130px] border-blue-500/20">
                    <span className="text-2xl mb-2 block">{item.icon}</span>
                    <div className="text-blue-400 font-bold text-xs mb-1">Étape {item.step}</div>
                    <p className="text-white text-xs font-medium">{item.text}</p>
                  </div>
                  {index < 2 && (
                    <ArrowRight className="hidden sm:block w-5 h-5 text-zinc-600 flex-shrink-0" />
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Guide 2: Créer une recette travaux */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <span className="text-emerald-400 text-sm font-semibold uppercase tracking-wider">
                En pratique
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mt-2">
                Créer une recette travaux en 3 étapes
              </h2>
            </motion.div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              {[
                { step: '1', text: 'Décrivez votre projet', icon: '✏️' },
                { step: '2', text: 'Léon génère le guide', icon: '📋' },
                { step: '3', text: 'Suivez étape par étape', icon: '🛠️' }
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="glass-card p-4 sm:p-5 text-center flex-shrink-0 min-w-[130px] border-emerald-500/20">
                    <span className="text-2xl mb-2 block">{item.icon}</span>
                    <div className="text-emerald-400 font-bold text-xs mb-1">Étape {item.step}</div>
                    <p className="text-white text-xs font-medium">{item.text}</p>
                  </div>
                  {index < 2 && (
                    <ArrowRight className="hidden sm:block w-5 h-5 text-zinc-600 flex-shrink-0" />
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Guide 3: Diagnostic photo */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider">
                En pratique
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mt-2">
                Diagnostic photo en 3 étapes
              </h2>
            </motion.div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              {[
                { step: '1', text: 'Prenez une photo', icon: '📸' },
                { step: '2', text: 'Léon analyse l\'image', icon: '🔎' },
                { step: '3', text: 'Recevez les recommandations', icon: '💡' }
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="glass-card p-4 sm:p-5 text-center flex-shrink-0 min-w-[130px] border-purple-500/20">
                    <span className="text-2xl mb-2 block">{item.icon}</span>
                    <div className="text-purple-400 font-bold text-xs mb-1">Étape {item.step}</div>
                    <p className="text-white text-xs font-medium">{item.text}</p>
                  </div>
                  {index < 2 && (
                    <ArrowRight className="hidden sm:block w-5 h-5 text-zinc-600 flex-shrink-0" />
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Guide 4: Poser une question */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <span className="text-gold-400 text-sm font-semibold uppercase tracking-wider">
                En pratique
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mt-2">
                Poser une question en 3 étapes
              </h2>
            </motion.div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              {[
                { step: '1', text: 'Posez votre question', icon: '💬' },
                { step: '2', text: 'Léon réfléchit', icon: '🤔' },
                { step: '3', text: 'Réponse claire et pratique', icon: '✨' }
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="glass-card p-4 sm:p-5 text-center flex-shrink-0 min-w-[130px] border-gold-500/20">
                    <span className="text-2xl mb-2 block">{item.icon}</span>
                    <div className="text-gold-400 font-bold text-xs mb-1">Étape {item.step}</div>
                    <p className="text-white text-xs font-medium">{item.text}</p>
                  </div>
                  {index < 2 && (
                    <ArrowRight className="hidden sm:block w-5 h-5 text-zinc-600 flex-shrink-0" />
                  )}
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Section 5: Call to Action */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto text-center"
        >
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-4">
            Prêt à sécuriser vos travaux ?
          </h2>
          
          <p className="text-zinc-400 mb-8">
            Testez Léon gratuitement. Aucune carte bancaire requise.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleGetStarted}
              className="px-8 py-6 text-lg bg-gradient-to-r from-gold-500 to-amber-500 hover:from-gold-400 hover:to-amber-400 text-black font-bold"
            >
              <FileSearch className="w-5 h-5 mr-2" />
              Analyser mon premier devis
            </Button>
            
            <Button
              onClick={() => navigate(user ? '/chat' : '/auth')}
              variant="outline"
              className="px-8 py-6 text-lg border-zinc-700 text-white hover:bg-zinc-800"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Poser une question
            </Button>
          </div>
          
          <p className="text-zinc-600 text-xs mt-6">
            Gratuit • Sans engagement • Résultats immédiats
          </p>
        </motion.div>
      </section>

    </div>
  );
}
